// The auth client calls are passed in, so these can be tested without the browser-only client.

type VerifyMagicLink = (options: {
  query: { token: string };
  fetchOptions: { redirect: "manual" };
}) => Promise<{ data: unknown; error: unknown }>;

/**
 * Spends a magic link token, which creates the session for this browser.
 *
 * better-auth only answers with JSON on success; every failure (invalid, expired, already used) is a
 * redirect carrying `?error=`. The redirect is left unfollowed so a failure is not mistaken for the
 * page it points at.
 */
export async function verifyMagicLinkToken(
  verify: VerifyMagicLink,
  token: string,
): Promise<boolean> {
  try {
    const { data, error } = await verify({
      query: { token },
      fetchOptions: { redirect: "manual" },
    });

    return !error && typeof data === "object" && data !== null;
  } catch {
    return false;
  }
}

type SignInWithEmailOtp = (options: {
  email: string;
  otp: string;
}) => Promise<{ data: unknown; error: unknown }>;

/**
 * Spends the one-time code emailed alongside a magic link, which creates the session for this
 * browser.
 *
 * The code is a better-auth email OTP, so it is checked against the email it was sent to. A wrong,
 * expired or used-up code comes back as an error.
 */
export async function verifyMagicLinkCode(
  signIn: SignInWithEmailOtp,
  email: string,
  code: string,
): Promise<boolean> {
  try {
    const { data, error } = await signIn({ email, otp: code });

    return !error && typeof data === "object" && data !== null;
  } catch {
    return false;
  }
}

/**
 * Loads the current session, or `null` when there is none.
 *
 * better-auth hands a non-JSON 200 back as text, so anything that is not an object counts as none.
 */
export async function loadSession<TSession extends object>(
  getSession: () => Promise<{ data: TSession | null; error: unknown }>,
): Promise<TSession | null> {
  try {
    const { data, error } = await getSession();

    return !error && typeof data === "object" ? data : null;
  } catch {
    return null;
  }
}
